import React, { useEffect, useState, useCallback } from 'react';
import DealerLayout from '../components/DealerLayout';
import api from '../api/axios';
import { C } from '../theme';

export default function DealerWallet() {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab,     setTab]     = useState('credits');  // 'credits' | 'usage'

  // Pay Now (CCAvenue top-up)
  const [payOpen,  setPayOpen]  = useState(false);
  const [amount,   setAmount]   = useState('');
  const [paying,   setPaying]   = useState(false);
  const [payErr,   setPayErr]   = useState('');
  const [banner,   setBanner]   = useState(null);   // { ok, text } after redirect back

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get('/api/dealer/credits'); setData(r.data); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Handle CCAvenue redirect-back (?topup=success&order=…&msg=…)
  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    const status = q.get('topup');
    if (!status) return;
    const msg = q.get('msg');
    if (status === 'success') setBanner({ ok: true, text: 'Payment successful — your wallet has been topped up ✓' });
    else if (status === 'cancelled') setBanner({ ok: false, text: 'Payment was cancelled.' });
    else setBanner({ ok: false, text: msg || 'Payment failed. No amount was charged.' });
    // clear the query string so a refresh doesn't re-show it
    window.history.replaceState({}, '', window.location.pathname);
    load();
    const t = setTimeout(() => setBanner(null), 8000);
    return () => clearTimeout(t);
  }, [load]);

  // Build & submit an auto-posting form to CCAvenue's gateway
  const startPayment = async () => {
    const amt = Number(amount);
    if (!amt || amt <= 0) { setPayErr('Enter a valid amount.'); return; }
    setPaying(true); setPayErr('');
    try {
      const r = await api.post('/api/dealer/wallet/topup/initiate', { amount: amt });
      const { action_url, access_code, enc_request } = r.data;
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = action_url;
      [['encRequest', enc_request], ['access_code', access_code]].forEach(([k, v]) => {
        const i = document.createElement('input');
        i.type = 'hidden'; i.name = k; i.value = v;
        form.appendChild(i);
      });
      document.body.appendChild(form);
      form.submit();   // leaves the app for CCAvenue's hosted page
    } catch (e) {
      setPayErr(e?.response?.data?.detail || 'Could not start payment.');
      setPaying(false);
    }
  };

  const fmtAmt = (n, sign) => {
    const abs = Math.abs(Number(n || 0));
    const str = '₹' + abs.toLocaleString('en-IN', { minimumFractionDigits: 2 });
    return sign ? (Number(n) >= 0 ? '+' + str : '−' + str) : str;
  };

  const txns   = data?.transactions || [];
  const credits = txns.filter(t => t.type === 'credit');
  const debits  = txns.filter(t => t.type === 'debit');
  const balance = data?.balance ?? 0;

  const totalCredited = credits.reduce((s, t) => s + t.amount, 0);
  const totalUsed     = debits.reduce((s, t)  => s + t.amount, 0);

  return (
    <DealerLayout title="Wallet">

      {/* Redirect-back banner */}
      {banner && (
        <div style={{
          padding: '12px 18px', borderRadius: '10px', marginBottom: '16px',
          fontSize: '13px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '8px',
          background: banner.ok ? '#ECFDF5' : '#FEF2F2',
          color:      banner.ok ? '#065F46' : '#991B1B',
          border: `1px solid ${banner.ok ? '#A7F3D0' : '#FECACA'}`,
        }}>
          {banner.ok ? '✓' : '⚠'} {banner.text}
        </div>
      )}

      {/* Balance card */}
      <div style={{
        background: 'linear-gradient(135deg, #3B0764 0%, #1E1040 100%)',
        borderRadius: '16px', padding: '28px 32px', marginBottom: '24px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px',
      }}>
        <div>
          <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.45)', textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: '8px' }}>Current Balance</div>
          <div style={{ fontSize: '36px', fontWeight: 800, color: balance >= 0 ? '#A7F3D0' : '#FCA5A5', letterSpacing: '-0.5px' }}>
            {fmtAmt(balance)}
          </div>
          <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.35)', marginTop: '6px' }}>
            {txns.length} transaction{txns.length !== 1 ? 's' : ''} total
          </div>
          <button onClick={() => { setPayOpen(true); setAmount(''); setPayErr(''); }} style={{
            marginTop: '16px', padding: '10px 22px', borderRadius: '10px', border: 'none', cursor: 'pointer',
            background: 'linear-gradient(135deg,#25D366,#128C7E)', color: '#fff', fontSize: '14px', fontWeight: 800,
            boxShadow: '0 3px 12px rgba(18,140,126,0.4)',
          }}>
            💳 Pay Now — Add Credit
          </button>
        </div>
        <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
          {[
            { label: 'Total Credited', val: totalCredited, color: '#A7F3D0', sign: '+' },
            { label: 'Total Used',     val: totalUsed,     color: '#FCA5A5', sign: '−' },
          ].map(s => (
            <div key={s.label} style={{ textAlign: 'right' }}>
              <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '4px' }}>{s.label}</div>
              <div style={{ fontSize: '20px', fontWeight: 700, color: s.color }}>{s.sign}₹{Math.abs(s.val).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Summary chips */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '22px', flexWrap: 'wrap' }}>
        {[
          { label: 'Credits',    val: credits.length, color: '#059669', bg: '#ECFDF5', border: '#6EE7B7' },
          { label: 'Usage',      val: debits.length,  color: '#DC2626', bg: '#FEF2F2', border: '#FECACA' },
        ].map(s => (
          <div key={s.label} style={{ background: s.bg, border: `1.5px solid ${s.border}`, borderRadius: '10px', padding: '12px 20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div>
              <div style={{ fontSize: '20px', fontWeight: 800, color: s.color }}>{s.val}</div>
              <div style={{ fontSize: '11px', color: C.textSub, fontWeight: 600 }}>{s.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '4px', borderBottom: `2px solid ${C.border}`, marginBottom: '0', paddingBottom: '0' }}>
        {[
          { key: 'credits', label: '💰 Credit History', count: credits.length },
          { key: 'usage',   label: '📤 Credit Usage',   count: debits.length  },
        ].map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} style={{
            padding: '10px 20px', border: 'none', cursor: 'pointer', fontWeight: 700,
            fontSize: '13px', borderRadius: '8px 8px 0 0', marginBottom: '-2px',
            background: tab === t.key ? C.white : 'transparent',
            color: tab === t.key ? C.primary : C.textSub,
            borderBottom: tab === t.key ? `2px solid ${C.primary}` : '2px solid transparent',
          }}>
            {t.label}
            <span style={{
              marginLeft: '8px', fontSize: '11px', fontWeight: 700,
              background: tab === t.key ? C.primaryLight : '#F3F4F6',
              color: tab === t.key ? C.primaryDeep : C.textSub,
              padding: '1px 7px', borderRadius: '10px',
            }}>{t.count}</span>
          </button>
        ))}
      </div>

      {/* Transaction table */}
      <div style={{ background: C.white, borderRadius: '0 12px 12px 12px', border: `1px solid ${C.border}`, borderTop: 'none' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '52px', color: C.textSub, fontSize: '13px' }}>Loading…</div>
        ) : (
          <TxnTable rows={tab === 'credits' ? credits : debits} type={tab === 'credits' ? 'credit' : 'debit'} fmtAmt={fmtAmt} />
        )}
      </div>

      {/* ── Pay Now (CCAvenue) modal ── */}
      {payOpen && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(20,10,30,0.65)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000, padding: '16px' }}>
          <div style={{ background: '#fff', borderRadius: '16px', width: '100%', maxWidth: '420px', boxShadow: '0 24px 80px rgba(0,0,0,0.3)', overflow: 'hidden' }}>
            <div style={{ padding: '18px 22px', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#F0FDF4' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{ width: '34px', height: '34px', borderRadius: '9px', background: 'linear-gradient(135deg,#25D366,#128C7E)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '17px', color: '#fff' }}>💳</div>
                <div style={{ fontSize: '15px', fontWeight: 800, color: '#065F46' }}>Add Wallet Credit</div>
              </div>
              <button onClick={() => setPayOpen(false)} disabled={paying} style={{ background: 'none', border: 'none', fontSize: '19px', cursor: 'pointer', color: C.textSub }}>✕</button>
            </div>
            <div style={{ padding: '22px' }}>
              <label style={{ fontSize: '12px', fontWeight: 700, color: C.textMain, display: 'block', marginBottom: '7px' }}>Amount to add (₹)</label>
              <div style={{ position: 'relative' }}>
                <span style={{ position: 'absolute', left: '13px', top: '50%', transform: 'translateY(-50%)', fontSize: '17px', fontWeight: 800, color: '#128C7E' }}>₹</span>
                <input type="number" min="1" step="1" value={amount} autoFocus
                  onChange={e => { setAmount(e.target.value); setPayErr(''); }}
                  onKeyDown={e => { if (e.key === 'Enter') startPayment(); }}
                  placeholder="0"
                  style={{ width: '100%', padding: '12px 14px 12px 34px', boxSizing: 'border-box', border: `1.5px solid ${payErr ? C.danger : C.border}`, borderRadius: '10px', fontSize: '18px', fontWeight: 700, outline: 'none' }} />
              </div>
              <div style={{ display: 'flex', gap: '8px', marginTop: '10px', flexWrap: 'wrap' }}>
                {[500, 1000, 2000, 5000].map(v => (
                  <button key={v} onClick={() => { setAmount(String(v)); setPayErr(''); }} style={{ padding: '6px 12px', borderRadius: '8px', border: `1px solid ${C.border}`, background: Number(amount) === v ? '#DCFCE7' : C.white, color: Number(amount) === v ? '#128C7E' : C.textSub, fontSize: '12px', fontWeight: 700, cursor: 'pointer' }}>
                    ₹{v.toLocaleString('en-IN')}
                  </button>
                ))}
              </div>
              {payErr && <div style={{ color: C.danger, fontSize: '12px', marginTop: '10px', fontWeight: 600 }}>{payErr}</div>}
              <div style={{ marginTop: '14px', padding: '10px 13px', background: '#F5F3FF', borderRadius: '9px', fontSize: '11px', color: '#5B21B6', lineHeight: 1.5 }}>
                🔒 You'll be redirected to the secure CCAvenue payment page of your mapped business. Your wallet is credited automatically once payment succeeds.
              </div>
            </div>
            <div style={{ padding: '14px 22px', borderTop: `1px solid ${C.border}`, display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button onClick={() => setPayOpen(false)} disabled={paying} style={{ padding: '10px 18px', background: '#F3F4F6', color: C.textSub, border: `1px solid ${C.border}`, borderRadius: '9px', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
              <button onClick={startPayment} disabled={paying} style={{ padding: '10px 24px', background: paying ? '#9CA3AF' : 'linear-gradient(135deg,#25D366,#128C7E)', color: '#fff', border: 'none', borderRadius: '9px', fontSize: '13px', fontWeight: 800, cursor: paying ? 'not-allowed' : 'pointer' }}>
                {paying ? 'Redirecting…' : `Pay ₹${(Number(amount) || 0).toLocaleString('en-IN')}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </DealerLayout>
  );
}

function TxnTable({ rows, type, fmtAmt }) {
  const isCredit = type === 'credit';
  const amtColor = isCredit ? '#059669' : '#DC2626';
  const amtBg    = isCredit ? '#ECFDF5' : '#FEF2F2';
  const amtSign  = isCredit ? '+' : '−';

  if (rows.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '52px', color: C.textSub, fontSize: '13px' }}>
        {isCredit ? 'No credits yet.' : 'No usage yet.'}
      </div>
    );
  }

  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '620px' }}>
        <thead>
          <tr style={{ background: C.primaryBg, borderBottom: `2px solid ${C.border}` }}>
            {['Date & Time', 'Description', isCredit ? 'Payment Mode' : 'Reference', 'Amount', 'Balance After'].map(h => (
              <th key={h} style={th}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((t, i) => (
            <tr key={t.id} style={{ background: i % 2 === 0 ? C.white : (isCredit ? '#FAFFFD' : '#FFFAFA'), borderBottom: `1px solid ${C.border}` }}>
              <td style={td}>
                <span style={{ fontSize: '12px', color: C.textSub, fontFamily: 'monospace' }}>{t.created_at || '—'}</span>
              </td>
              <td style={td}>
                <span style={{ fontSize: '13px', color: C.textMain }}>{t.description || '—'}</span>
              </td>
              <td style={td}>
                <span style={{ fontSize: '12px', color: C.textSub, fontWeight: 500 }}>
                  {isCredit ? (t.payment_mode || '—') : (t.reference || '—')}
                </span>
              </td>
              <td style={td}>
                <span style={{ fontWeight: 800, fontSize: '14px', color: amtColor, background: amtBg, padding: '3px 10px', borderRadius: '8px' }}>
                  {amtSign}₹{Math.abs(t.amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                </span>
              </td>
              <td style={td}>
                <span style={{ fontWeight: 700, fontSize: '13px', color: t.balance_after >= 0 ? '#059669' : '#DC2626' }}>
                  ₹{Number(t.balance_after).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const th       = { textAlign: 'left', padding: '11px 14px', fontSize: '11px', fontWeight: 700, color: C.textSub, textTransform: 'uppercase', letterSpacing: '0.5px' };
const td       = { padding: '12px 14px', fontSize: '13px', color: C.textMain, verticalAlign: 'middle' };
